import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { Landmark, ChevronRight, RefreshCw, Download, Loader2, AlertTriangle, Filter } from 'lucide-react';
import CustomDatePicker from '../../components/CustomDatePicker';

import {
    fetchVendorSuggestions, fetchVendorBalanceReport,
    clearVendorSuggestions, clearVendorBalanceReport,
    selectVendorSuggestions, selectVendorSuggestionsLoading,
    selectVendorBalanceReport, selectVendorBalanceReportLoading, selectVendorBalanceReportError,
} from '../../slices/ReportsSlice/vendorBalanceReportSlice';

const inputCls = 'w-full px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent';

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** CustomDatePicker's onChange always hands back a Date object (not a formatted string) */
const toDDMonYYYY = (val) => {
    if (!val) return '';
    const d = val instanceof Date ? val : new Date(val);
    if (isNaN(d.getTime())) return '';
    return `${String(d.getDate()).padStart(2, '0')}-${MONTH_ABBR[d.getMonth()]}-${d.getFullYear()}`;
};

const VENDOR_TYPES = [
    { id: 'Supplier', label: 'Supplier' },
    { id: 'ServiceProvider', label: 'Service Provider' },
];

/** VendorName from the backend is "CODE , Name" (spGetVendorCodesandNames embeds the code) — vendor codes are always 8 chars. */
const parseVendorCode = (text) => {
    if (!text) return '';
    const code = text.split(',')[0].trim();
    return code.length === 8 ? code : '';
};

const NumCell = ({ value }) => (
    <td className="px-2 py-1 text-xs text-right text-gray-700 dark:text-gray-200 whitespace-nowrap">
        {parseFloat(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
    </td>
);

const VendorBalanceReport = () => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID;
    const uid = userData?.UID || userData?.uid;

    const suggestions = useSelector(selectVendorSuggestions);
    const suggestionsLoading = useSelector(selectVendorSuggestionsLoading);
    const report = useSelector(selectVendorBalanceReport);
    const reportLoading = useSelector(selectVendorBalanceReportLoading);
    const reportError = useSelector(selectVendorBalanceReportError);

    // Legacy's two radios are mislabeled ("Normal View"/"Detail View" swapped vs what they actually
    // do — see spGetVendorBalanceSummaryReport's own comment: null VendorCode = "Normal" aggregate
    // view with opening balances, a specific VendorCode = "Detail" per-vendor view without them).
    // Relabeled here to match actual behaviour; functionality is unchanged.
    const [reportMode, setReportMode] = useState(''); // 'summary' | 'vendor'

    // Summary mode (all vendors, aggregated over the date range)
    const [summaryFromDate, setSummaryFromDate] = useState(null);
    const [summaryToDate, setSummaryToDate] = useState(null);

    // Vendor mode (single vendor lookup)
    const [vendorType, setVendorType] = useState('');
    const [vendorSearch, setVendorSearch] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [vendorFromDate, setVendorFromDate] = useState(null);
    const [vendorToDate, setVendorToDate] = useState(null);
    const suggestionBoxRef = useRef(null);
    const debounceRef = useRef(null);

    const [ranMode, setRanMode] = useState(null); // mode actually submitted, drives column set

    useEffect(() => () => clearTimeout(debounceRef.current), []);

    const handleReportModeChange = (mode) => {
        setReportMode(mode);
        dispatch(clearVendorBalanceReport());
        setRanMode(null);
    };

    // Mirrors legacy's FromdateChange(): suggests a ~12-month window ending on the last day of
    // that month, capped at today, and pins the To Date's minimum to the From Date chosen.
    const handleSummaryFromDateChange = (val) => {
        setSummaryFromDate(val);
        if (!(val instanceof Date) || isNaN(val.getTime())) return;
        const suggested = new Date(val.getFullYear(), val.getMonth() + 12, 0);
        const today = new Date();
        setSummaryToDate(suggested > today ? today : suggested);
    };

    const handleVendorTypeChange = (type) => {
        setVendorType(type);
        setVendorSearch('');
        setShowSuggestions(false);
        dispatch(clearVendorSuggestions());
    };

    const handleVendorSearchChange = (text) => {
        setVendorSearch(text.toUpperCase());
        clearTimeout(debounceRef.current);
        if (!vendorType) { toast.warning('Select Search By Vendor Type'); return; }
        if (!text.trim()) { dispatch(clearVendorSuggestions()); setShowSuggestions(false); return; }
        debounceRef.current = setTimeout(() => {
            dispatch(fetchVendorSuggestions({ UID: uid, RID: roleId, VendorType: vendorType, prefix: text.trim() }));
            setShowSuggestions(true);
        }, 300);
    };

    const selectVendorSuggestion = (name) => {
        setVendorSearch(name);
        setShowSuggestions(false);
    };

    const handleReset = () => {
        setReportMode('');
        setSummaryFromDate(null); setSummaryToDate(null);
        setVendorType(''); setVendorSearch(''); setShowSuggestions(false);
        setVendorFromDate(null); setVendorToDate(null);
        setRanMode(null);
        dispatch(clearVendorBalanceReport());
        dispatch(clearVendorSuggestions());
    };

    const runReport = () => {
        if (!reportMode) { toast.warning('Select Report Type'); return; }

        if (reportMode === 'summary') {
            if (!summaryFromDate) { toast.warning('Select From Date'); return; }
            if (!summaryToDate) { toast.warning('Select To Date'); return; }
            setRanMode('summary');
            dispatch(fetchVendorBalanceReport({ FromDate: toDDMonYYYY(summaryFromDate), ToDate: toDDMonYYYY(summaryToDate) }));
            return;
        }

        // reportMode === 'vendor'
        if (!vendorType) { toast.warning('Select Vendor Type'); return; }
        if (!vendorSearch.trim()) { toast.warning('Search Vendor'); return; }
        const vendorCode = parseVendorCode(vendorSearch);
        if (!vendorCode) { toast.warning('Invalid Vendor Search'); return; }
        if (!vendorFromDate) { toast.warning('Select From Date'); return; }
        if (!vendorToDate) { toast.warning('Select To Date'); return; }
        setRanMode('vendor');
        dispatch(fetchVendorBalanceReport({ FromDate: toDDMonYYYY(vendorFromDate), ToDate: toDDMonYYYY(vendorToDate), VendorCode: vendorCode }));
    };

    const handleRefresh = () => {
        if (ranMode === 'summary') {
            dispatch(fetchVendorBalanceReport({ FromDate: toDDMonYYYY(summaryFromDate), ToDate: toDDMonYYYY(summaryToDate) }));
        } else if (ranMode === 'vendor') {
            dispatch(fetchVendorBalanceReport({ FromDate: toDDMonYYYY(vendorFromDate), ToDate: toDDMonYYYY(vendorToDate), VendorCode: parseVendorCode(vendorSearch) }));
        }
    };

    const rows = report?.VendorDetailData || [];
    const showOpeningBal = ranMode === 'summary';

    const AMOUNT_KEYS = useMemo(() => {
        const base = ['BasicValue', 'BasicValuePaid', 'BasicValueBalance', 'RetentionValue', 'RetentionPaid', 'RetentionBalance', 'TdsValue', 'TdsPaid', 'TdsBalance', 'HoldValue', 'HoldPaid', 'HoldBalance'];
        return showOpeningBal ? [...base, 'BasicOpeningBal', 'RetentionOpeningBal', 'TdsOpeningBal'] : base;
    }, [showOpeningBal]);

    const totals = useMemo(() => rows.reduce((acc, r) => {
        AMOUNT_KEYS.forEach((k) => { acc[k] = (acc[k] || 0) + (parseFloat(r[k]) || 0); });
        return acc;
    }, {}), [rows, AMOUNT_KEYS]);

    const handleExport = () => {
        if (rows.length === 0) { toast.warning('No data to export'); return; }
        const exportRows = rows.map((r) => {
            const row = {
                'Vendor Code': r.VendorCode, 'Vendor Name': r.VendorName,
                'Basic Value': r.BasicValue, 'Basic Value Paid': r.BasicValuePaid,
            };
            if (showOpeningBal) row['Basic Value Opening Balance'] = r.BasicOpeningBal;
            row['Basic Value Balance'] = r.BasicValueBalance;
            row['Retention Value'] = r.RetentionValue; row['Retention Paid'] = r.RetentionPaid;
            if (showOpeningBal) row['Retention Opening Balance'] = r.RetentionOpeningBal;
            row['Retention Balance'] = r.RetentionBalance;
            row['Tds Value'] = r.TdsValue; row['Tds Paid'] = r.TdsPaid;
            if (showOpeningBal) row['Tds Opening Balance'] = r.TdsOpeningBal;
            row['Tds Balance'] = r.TdsBalance;
            row['Hold Value'] = r.HoldValue; row['Hold Paid'] = r.HoldPaid; row['Hold Balance'] = r.HoldBalance;
            return row;
        });
        const totalsRow = { 'Vendor Code': '', 'Vendor Name': 'TOTAL', ...totals };
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet([...exportRows, totalsRow]), 'Vendor Balance');
        XLSX.writeFile(wb, `Vendor_Balance_Summary_Report_${new Date().toISOString().slice(0, 10)}.xlsx`);
        toast.success('Excel downloaded');
    };

    return (
        <div className="space-y-3 p-4 text-xs">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <div className="p-1.5 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-lg">
                                <Landmark className="h-4 w-4 text-white" />
                            </div>
                            Periodic Vendor Payment
                        </h1>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">Vendor-wise basic/retention/TDS/hold value, paid amount and balance for a period</p>
                        <nav className="flex items-center gap-1.5 text-[11px] text-gray-400 mt-1">
                            <span>Reports</span><ChevronRight className="w-3 h-3" /><span className="text-gray-700 dark:text-gray-200">Vendor Balance Report</span>
                        </nav>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={handleRefresh} disabled={reportLoading || !ranMode} className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-60">
                            <RefreshCw className={`h-3.5 w-3.5 ${reportLoading ? 'animate-spin' : ''}`} />Refresh
                        </button>
                        <button onClick={handleExport} disabled={rows.length === 0} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-60">
                            <Download className="h-3.5 w-3.5" />Export Excel
                        </button>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-1.5 mb-2">
                    <Filter className="h-3.5 w-3.5 text-indigo-600" />
                    <h2 className="text-xs font-semibold text-gray-700 dark:text-gray-300">Filters</h2>
                </div>

                <div className="mb-3">
                    <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Report Type</label>
                    <div className="flex items-center gap-4">
                        <label className="flex items-center gap-1.5 cursor-pointer">
                            <input type="radio" name="reportMode" checked={reportMode === 'summary'} onChange={() => handleReportModeChange('summary')} />
                            <span>All Vendors (Summary)</span>
                        </label>
                        <label className="flex items-center gap-1.5 cursor-pointer">
                            <input type="radio" name="reportMode" checked={reportMode === 'vendor'} onChange={() => handleReportModeChange('vendor')} />
                            <span>Specific Vendor (Detail)</span>
                        </label>
                    </div>
                </div>

                {reportMode === 'summary' && (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                        <div>
                            <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">From Date</label>
                            <CustomDatePicker value={summaryFromDate} onChange={handleSummaryFromDateChange} maxDate={new Date()} />
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">To Date</label>
                            <CustomDatePicker value={summaryToDate} onChange={setSummaryToDate} minDate={summaryFromDate} maxDate={new Date()} />
                        </div>
                        <div className="flex gap-2 md:col-span-2">
                            <button onClick={runReport} disabled={reportLoading} className="flex-1 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-semibold transition-colors disabled:opacity-60">View</button>
                            <button onClick={handleReset} className="flex-1 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-xs font-semibold transition-colors">Reset</button>
                        </div>
                    </div>
                )}

                {reportMode === 'vendor' && (
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
                        <div>
                            <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Vendor Type</label>
                            <select className={inputCls} value={vendorType} onChange={(e) => handleVendorTypeChange(e.target.value)}>
                                <option value="">-Please Select-</option>
                                {VENDOR_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                            </select>
                        </div>
                        <div className="relative md:col-span-2" ref={suggestionBoxRef}>
                            <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Search Vendor Id/Name</label>
                            <input
                                type="text"
                                className={inputCls}
                                style={{ textTransform: 'uppercase' }}
                                value={vendorSearch}
                                disabled={!vendorType}
                                onChange={(e) => handleVendorSearchChange(e.target.value)}
                                onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
                                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                                placeholder={vendorType ? 'Type vendor code or name…' : 'Select Vendor Type first'}
                            />
                            {showSuggestions && (
                                <div className="absolute z-20 mt-1 w-full max-h-52 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
                                    {suggestionsLoading ? (
                                        <div className="px-3 py-2 text-gray-400 flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" />Searching…</div>
                                    ) : suggestions.length === 0 ? (
                                        <div className="px-3 py-2 text-gray-400">No matches</div>
                                    ) : (
                                        suggestions.map((v, i) => (
                                            <div key={i} className="px-3 py-1.5 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 cursor-pointer" onMouseDown={() => selectVendorSuggestion(v.VendorName)}>
                                                {v.VendorName}
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">From Date</label>
                            <CustomDatePicker value={vendorFromDate} onChange={(v) => { setVendorFromDate(v); }} maxDate={new Date()} />
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">To Date</label>
                            <CustomDatePicker value={vendorToDate} onChange={setVendorToDate} minDate={vendorFromDate} maxDate={new Date()} />
                        </div>
                        <div className="flex gap-2 md:col-span-5">
                            <button onClick={runReport} disabled={reportLoading} className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-semibold transition-colors disabled:opacity-60">View</button>
                            <button onClick={handleReset} className="px-4 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-xs font-semibold transition-colors">Reset</button>
                        </div>
                    </div>
                )}
            </div>

            {/* Report */}
            {reportLoading ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 flex items-center justify-center gap-3 py-10">
                    <Loader2 className="h-6 w-6 animate-spin text-indigo-600" /><span className="text-xs text-gray-500 dark:text-gray-400">Loading report…</span>
                </div>
            ) : reportError ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 flex items-center justify-center gap-3 py-10 text-red-500">
                    <AlertTriangle className="h-5 w-5" /><span className="text-xs">{reportError}</span>
                </div>
            ) : ranMode && rows.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 py-10 text-center text-xs text-gray-400">No Data Found</div>
            ) : rows.length > 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
                        <h2 className="text-xs font-bold text-gray-700 dark:text-gray-300">Vendor Balance Summary</h2>
                    </div>
                    <div className="overflow-auto max-h-[70vh]">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gradient-to-r from-indigo-600 to-violet-600 sticky top-0 z-10">
                                <tr>
                                    <th className="px-2 py-1.5 text-left text-[10px] font-bold text-white uppercase whitespace-nowrap">Vendor Code</th>
                                    <th className="px-2 py-1.5 text-left text-[10px] font-bold text-white uppercase whitespace-nowrap">Vendor Name</th>
                                    <th className="px-2 py-1.5 text-center text-[10px] font-bold text-white uppercase whitespace-nowrap">Basic Value</th>
                                    <th className="px-2 py-1.5 text-center text-[10px] font-bold text-white uppercase whitespace-nowrap">Basic Value Paid</th>
                                    {showOpeningBal && <th className="px-2 py-1.5 text-center text-[10px] font-bold text-white uppercase whitespace-nowrap">Basic Opening Bal.</th>}
                                    <th className="px-2 py-1.5 text-center text-[10px] font-bold text-white uppercase whitespace-nowrap">Basic Value Balance</th>
                                    <th className="px-2 py-1.5 text-center text-[10px] font-bold text-white uppercase whitespace-nowrap">Retention Value</th>
                                    <th className="px-2 py-1.5 text-center text-[10px] font-bold text-white uppercase whitespace-nowrap">Retention Paid</th>
                                    {showOpeningBal && <th className="px-2 py-1.5 text-center text-[10px] font-bold text-white uppercase whitespace-nowrap">Retention Opening Bal.</th>}
                                    <th className="px-2 py-1.5 text-center text-[10px] font-bold text-white uppercase whitespace-nowrap">Retention Balance</th>
                                    <th className="px-2 py-1.5 text-center text-[10px] font-bold text-white uppercase whitespace-nowrap">Tds Value</th>
                                    <th className="px-2 py-1.5 text-center text-[10px] font-bold text-white uppercase whitespace-nowrap">Tds Paid</th>
                                    {showOpeningBal && <th className="px-2 py-1.5 text-center text-[10px] font-bold text-white uppercase whitespace-nowrap">Tds Opening Bal.</th>}
                                    <th className="px-2 py-1.5 text-center text-[10px] font-bold text-white uppercase whitespace-nowrap">Tds Balance</th>
                                    <th className="px-2 py-1.5 text-center text-[10px] font-bold text-white uppercase whitespace-nowrap">Hold Value</th>
                                    <th className="px-2 py-1.5 text-center text-[10px] font-bold text-white uppercase whitespace-nowrap">Hold Paid</th>
                                    <th className="px-2 py-1.5 text-center text-[10px] font-bold text-white uppercase whitespace-nowrap">Hold Balance</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {rows.map((r, i) => (
                                    <tr key={i} className="hover:bg-indigo-50 dark:hover:bg-indigo-900/10 transition-colors">
                                        <td className="px-2 py-1 text-xs text-gray-800 dark:text-gray-100 whitespace-nowrap">{r.VendorCode}</td>
                                        <td className="px-2 py-1 text-xs text-gray-800 dark:text-gray-100">{r.VendorName}</td>
                                        <NumCell value={r.BasicValue} /><NumCell value={r.BasicValuePaid} />
                                        {showOpeningBal && <NumCell value={r.BasicOpeningBal} />}
                                        <NumCell value={r.BasicValueBalance} />
                                        <NumCell value={r.RetentionValue} /><NumCell value={r.RetentionPaid} />
                                        {showOpeningBal && <NumCell value={r.RetentionOpeningBal} />}
                                        <NumCell value={r.RetentionBalance} />
                                        <NumCell value={r.TdsValue} /><NumCell value={r.TdsPaid} />
                                        {showOpeningBal && <NumCell value={r.TdsOpeningBal} />}
                                        <NumCell value={r.TdsBalance} />
                                        <NumCell value={r.HoldValue} /><NumCell value={r.HoldPaid} /><NumCell value={r.HoldBalance} />
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="bg-gray-100 dark:bg-gray-900/50 font-bold">
                                    <td className="px-2 py-1 text-xs" colSpan={2}>TOTAL</td>
                                    <NumCell value={totals.BasicValue} /><NumCell value={totals.BasicValuePaid} />
                                    {showOpeningBal && <NumCell value={totals.BasicOpeningBal} />}
                                    <NumCell value={totals.BasicValueBalance} />
                                    <NumCell value={totals.RetentionValue} /><NumCell value={totals.RetentionPaid} />
                                    {showOpeningBal && <NumCell value={totals.RetentionOpeningBal} />}
                                    <NumCell value={totals.RetentionBalance} />
                                    <NumCell value={totals.TdsValue} /><NumCell value={totals.TdsPaid} />
                                    {showOpeningBal && <NumCell value={totals.TdsOpeningBal} />}
                                    <NumCell value={totals.TdsBalance} />
                                    <NumCell value={totals.HoldValue} /><NumCell value={totals.HoldPaid} /><NumCell value={totals.HoldBalance} />
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default VendorBalanceReport;
